import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { MatDialog, MatDialogRef, MatSnackBar } from "@angular/material";
import { Router } from "@angular/router";
import { Resource } from "@app/shared/sdk";
import { fuseAnimations } from "@fuse/animations";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { DateFormatPipe } from "angular2-moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as moment from "moment";

import {
  getTitleTextStyle,
  getTitlePositionStyle,
  getCategoryTextStyle,
  getCategoryPositionStyle,
  getEffectiveTitleOpt,
  isSameEdge,
  getSameEdgeContainerStyle
} from "@app/shared/utils/game-card-styles.util";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";
import { GameService } from "../game.service";
import { environment } from "@env/environment";
import { GameApi } from "@app/shared/sdk/services/custom/Game";
import { CsvService } from "@app/csv.service";

@Component({
  selector: "app-game-card",
  templateUrl: "./game-card.component.html",
  styleUrls: ["./game-card.component.scss"],
  animations: fuseAnimations,
  providers: [DateFormatPipe],
  encapsulation: ViewEncapsulation.None
})
export class GameCardComponent implements OnInit, OnChanges {
  @Input("gameType") gameType: string = "currentGames";
  @Input("games") games: any[] = [];
  @Input("noOfGames") noOfGames: number = 0;
  @Input("page") page: number = 0;
  @Input("paginationId") paginationId: string = ''

  resources: Resource[] = [];

  privateIcon: Resource = new Resource();
  shareIcon: Resource = new Resource();

  ConfirmDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  s3BucketUrl: string = environment.s3BucketUrl;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    private gameApi: GameApi,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private gamesService: GameService,
    private fuseSplashScreen: FuseSplashScreenService,
    private router: Router,
    private csvService: CsvService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    this.resources = [...this.gamesService.resources];
    if (this.resources.length !== 0) {
      if (this.resources.find((el) => el.key === "privateIcon"))
        this.privateIcon = this.resources.find(
          (el) => el.key === "privateIcon"
        );

      if (this.resources.find((el) => el.key === "shareIcon"))
        this.shareIcon = this.resources.find((el) => el.key === "shareIcon");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.noOfGames && changes.noOfGames.currentValue !== undefined) {
      this.noOfGames = changes.noOfGames.currentValue;
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  handleDeleteGame = (game: any) => {
    if (this.gameType === 'endedGames') {
      this.deleteEntireGameFamily(game);
    } else {
      this.deleteGame(game.id);
    }
  };

  /**
   * Deletes ONE specific game version by id. Used by:
   * - Current Games' main Delete button (single game, no family concept)
   * - The "x" button next to a specific version inside the Restart
   *   dropdown (isRelatedGame = true)
   */
  deleteGame = (id: string, isRelatedGame = false) => {
    this.ConfirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: "Are you sure you want to Delete?" },
    });
    this.ConfirmDialogRef.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (!result) return;
        this.fuseSplashScreen.show();
        try {
          let response = await this.gameApi.deleteById(id).toPromise();
          if (isRelatedGame) await this.getDuplicateGames(this.page); // was hardcoded page 1 — fixed to preserve current page
          this.fuseSplashScreen.hide();
          if (!response) return;
          this.games = [...this.games.filter((el) => el.id !== id)];
          this.snackBar.open("Game Deleted", "OK", {
            verticalPosition: "top", duration: 2000,
          });
        } catch (error) {
          this.fuseSplashScreen.hide();
          console.log(error);
          throw error;
        }
      });
  };

  /**
   * Deletes the ORIGINAL game AND every ended restart version of it —
   * triggered only by the main card's Delete button on Ended Games.
   * This intentionally consumes `relatedGames` (date-filtered), NOT
   * `allRelatedGames` (unfiltered, display-only): a version still running
   * in Current Games is shown in the dropdown but can never be deleted
   * from here.
   */
  deleteEntireGameFamily = (game: any) => {
    const relatedCount = (game.relatedGames && game.relatedGames.length) || 0;
    const liveCount =
      ((game.allRelatedGames || []).filter((v: any) => v.isLive)).length;
    const liveNote = liveCount > 0
      ? ` ${liveCount} still-running version(s) will NOT be deleted.`
      : '';

    this.ConfirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: relatedCount > 0
          ? `This will delete the original game and all ${relatedCount} restarted version(s).${liveNote} This cannot be undone. Are you sure?`
          : `Are you sure you want to Delete?${liveNote}`,
      },
    });

    this.ConfirmDialogRef.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (!result) return;

        this.fuseSplashScreen.show();
        try {
          const idsToDelete = [
            game.id,
            ...((game.relatedGames || []).map((rg: any) => rg.id)),
          ];

          // Sequential on purpose: the backend's delete cascade re-parents
          // each version's children to its nearest living ancestor, and
          // concurrent deletes can re-parent onto a version that is itself
          // mid-delete, leaving a dangling previousGameId.
          const results: { id: string; success: boolean }[] = [];
          for (const id of idsToDelete) {
            try {
              await this.gameApi.deleteById(id).toPromise();
              results.push({ id, success: true });
            } catch (err) {
              console.error(`Failed to delete game version ${id}:`, err);
              results.push({ id, success: false });
            }
          }

          const failures = results.filter(r => !r.success);

          this.games = [...this.games.filter((el) => el.id !== game.id)];
          await this.getDuplicateGames(this.page);

          this.fuseSplashScreen.hide();

          if (failures.length > 0) {
            this.snackBar.open(
              `Deleted, but ${failures.length} of ${idsToDelete.length} version(s) failed — please verify`,
              'OK', { verticalPosition: 'top', duration: 4000 }
            );
          } else {
            this.snackBar.open('Game and all versions deleted', 'OK', {
              verticalPosition: 'top', duration: 2000,
            });
          }
        } catch (error) {
          this.fuseSplashScreen.hide();
          console.error(error);
          throw error;
        }
      });
  };

  gameEdit(id: string, actionType: string) {
    if (id && actionType === "edit") {
      this.router.navigate(["/game/edit/" + id], {
        queryParams: { action: actionType },
      });
    } else if (id && actionType === "restart") {
      this.fuseSplashScreen.show();
      this.router.navigate(["/game/restart/" + id], {
        queryParams: { action: actionType },
      });
    }
  }

  getNextPageData = async (page) => {
    if (this.gameType === "currentGames") {
      this.getGames(page, {
        drawDate: { gte: moment().toJSON() },
      });
    } else {
      await this.getDuplicateGames(page);
    }
  }

  getDuplicateGames = async (page) => {
    if (this.gameType === 'endedGames') {
      this.page = page;
      const limit = 6;
      const skip = (page - 1) * limit;
      try {
        this.fuseSplashScreen.show();
        let response: any = await this.gameApi.gamesWithDuplicateData(limit, skip).toPromise();
        if (!response && response.totalGames === 0) {
          this.fuseSplashScreen.hide();
          return;
        }
        for (let i = 0; i < response.games.length; i++) {
          response.games[i].isTab = "info";
          response.games[i].isCountDown = response.games[i].sharingContentCountdown !== null;
          response.games[i].isPulsate = false;
        }
        this.games = [...response.games];
        if (this.gameType === 'endedGames')
          this.noOfGames = response.totalGames;
        this.fuseSplashScreen.hide();
      } catch (error) {
        this.fuseSplashScreen.hide();
        throw error;
      }
    }
  }

  getGames = async (page: number, filter) => {
    this.page = page;
    const limit = 6;
    const skip = (page - 1) * limit;

    try {
      this.fuseSplashScreen.show();
      let games: any[] = await this.gamesService
        .getGames(skip, limit, filter)
        .toPromise();

      console.log('games....', games);

      this.fuseSplashScreen.hide();
      if (!games || games.length === 0) return;

      for (let i = 0; i < games.length; i++) {
        games[i].isTab = "info";
      }
      this.games = [];
      this.games = games;
      console.log('games....', this.games);
      await this.gettingGamesCount();
    } catch (error) {
      this.fuseSplashScreen.hide();
      console.log("error....", error);
      throw error;
    }
  };

  gettingGamesCount = async () => {
    const countFilter = this.gameType === 'currentGames' ? { drawDate: { gte: moment().toJSON() } } : {
      drawDate: { lte: moment().toJSON() },
    };
    const countResponse = await this.gameApi.count(countFilter).toPromise();

    if (!countResponse) throw new Error("Cannot Get Games Count");

    if (this.gameType === 'currentGames')
      this.noOfGames = countResponse.count;
  }

  isCountDownEnded(index: number, id: string) {
    let game = this.games.find(el => el.id === id);
    const providedDate = moment(game.drawDate);
    const currentDate = moment();
    if (game) {
      game.isPulsate = true;
      let timeOut = setTimeout(() => {
        game.isPulsate = false;
        game.isCountDown = false;
        if (providedDate.isBefore(currentDate)) {
          this.games = this.games.filter(el => el.id !== game.id);
        }
        clearTimeout(timeOut);
      }, 3000); //run this after 3 seconds
    }
  }

  flattenObject(obj, parentKey = '') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof value === 'object' && value !== null) {
        return acc.concat(this.flattenObject(value, newKey));
      }

      return acc.concat({ title: newKey, value });
    }, []);
  }

  downloadCSV(data) {
    const filename = 'game-data.csv';
    const dataArray = this.flattenObject(data)
    this.csvService.exportToCsv(filename, dataArray);
  }

  /**
   * Determines the background color based on specific conditions.
   * @returns {string} The calculated background color.
   */
  setBackgroundColor(game): string {
    // Check if the game menu toolbar is active
    if (game.isGameMenuToolbar) {
      return '#dcd7d7'; // When the toolbar is active, always return the specific color, regardless of the option
    } else if (game.gameMenuArt.menuArtOption === 'option_0') {
      return 'transparent'; // Return transparent background for 'option_0' only when the toolbar is not active
    } else if (game.gameMenuArt.menuArtOption === 'option_3' || game.gameMenuArt.menuArtOption === 'option_4') {
      return 'rgba(255, 255, 255, 0.5)'; // Return a different color when either 'option_3' or 'option_4' is selected
    } else {
      // Return transparent background when none of the conditions are met
      return 'transparent';
    }
  }

  getHeightForOption(optionName: string): string {
    if (optionName === 'large' || optionName === 'medium') {
      const width = 270;
      const height = width * 1.48;
      return `${height}px`;
    } else {
      return 'var(--width)';
    }
  }

  getMenuArt1stColor(game: any): string {
    if (!game) return '#62dd51';
    if (game.gameMenuArt && game.gameMenuArt.firstColor) return game.gameMenuArt.firstColor;
    if (game.firstColor) return game.firstColor;
    return '#62dd51';
  }

  getMenuArt2ndColor(game: any): string {
    if (!game) return '#f5fb5f';
    if (game.gameMenuArt && game.gameMenuArt.secondColor) return game.gameMenuArt.secondColor;
    if (game.secondColor) return game.secondColor;
    return '#f5fb5f';
  }

  getMenuCardBackground(game: any): string {
    if (!game) return 'linear-gradient(-225deg, white, white)';
    const shadowOpt = game.shadowOpt || (game.gameMenuArt && game.gameMenuArt.shadowOpt);
    const isGradient =
      game.inviteType === 'color-gradient' ||
      game.inviteType === 'layer' ||
      (shadowOpt && shadowOpt.transparentBg);
    const c1 = isGradient ? this.getMenuArt1stColor(game) : 'white';
    const c2 = isGradient ? this.getMenuArt2ndColor(game) : 'white';
    return `linear-gradient(-225deg, ${c1}, ${c2})`;
  }

  isTitleActive(game: any): boolean {
    if (!game || !game.title) return false;
    const opt = game.titleOpt || (game.gameMenuArt && game.gameMenuArt.titleOpt);
    return !!(opt && opt.isActive);
  }

  isCategoryActive(game: any): boolean {
    if (!game || !game.category || !game.category.name) return false;
    const opt = game.categoryOpt || (game.gameMenuArt && game.gameMenuArt.categoryOpt);
    return !!(opt && opt.isActive);
  }

  isEnterFreeActive(game: any): boolean {
    if (!game) return false;
    const opt = game.enterFreeOpt || (game.gameMenuArt && game.gameMenuArt.enterFreeOpt);
    return !!(opt && opt.isActive);
  }

  getEnterFreePillStyle(game: any): { [key: string]: string } {
    const opt = game && game.enterFreeOpt;
    const isBgActive = opt ? opt.isBackgroundActive !== false : true;
    const c1 = (opt && opt.firstColor) || '#62dd51';
    const c2 = (opt && opt.secondColor) || '#f5fb5f';
    return {
      'background-image': isBgActive
        ? `linear-gradient(to bottom, ${c1}, ${c2})`
        : 'linear-gradient(to bottom, #62dd51, #f5fb5f)',
    };
  }

  getEnterFreeTextColor(game: any): string {
    const opt = game && game.enterFreeOpt;
    if (opt && opt.isTextColorActive && opt.textColor) {
      return opt.textColor;
    }
    return '#000000';
  }

  isBackgroundBandVisible(game: any): boolean {
    const titleOpt = game && (game.titleOpt || (game.gameMenuArt && game.gameMenuArt.titleOpt));
    const categoryOpt = game && (game.categoryOpt || (game.gameMenuArt && game.gameMenuArt.categoryOpt));
    const titleBg = titleOpt ? (titleOpt.showBackground !== undefined ? !!titleOpt.showBackground : titleOpt.position === 'top') : false;
    const catBg = categoryOpt ? (categoryOpt.showBackground !== undefined ? !!categoryOpt.showBackground : categoryOpt.position === 'top') : false;
    return !!(titleBg || catBg);
  }

  isBottomPanelActive(game: any): boolean {
    if (!game) return false;
    return this.isTitleActive(game) || this.isCategoryActive(game) || this.isEnterFreeActive(game);
  }

  getTitleStyle(game: any): { [key: string]: string } {
    if (!this.isTitleActive(game)) {
      return { display: 'none' };
    }

    const titleOpt = game && (game.titleOpt || (game.gameMenuArt && game.gameMenuArt.titleOpt));
    const shadowOpt = game && (game.shadowOpt || (game.gameMenuArt && game.gameMenuArt.shadowOpt));
    const isEfx = titleOpt && titleOpt.efx;
    const titleColor =
      titleOpt && titleOpt.color && titleOpt.color !== '#c4bfbf'
        ? titleOpt.color
        : '#ffffff';

    const shadowColor =
      shadowOpt && shadowOpt.color && shadowOpt.color !== '#c4bfbf'
        ? shadowOpt.color
        : '#00ffff';

    const midColor = '#ffff00';

    if (isEfx) {
      return {
        color: titleColor,
        '--title-glow-core': titleColor,
        '--title-glow-mid': midColor,
        '--title-glow-mid-pulse': '#ffff88',
        '--title-glow-outer': shadowColor,
        'text-shadow': `0 0 10px ${titleColor}, 0 0 20px ${midColor}, 0 0 30px ${shadowColor}, 0 0 40px ${shadowColor}, 0 0 60px ${shadowColor}`,
        animation: 'floatTitleEfx 4s ease-in-out infinite alternate, pulseGlowTitle 6s ease-in-out infinite',
        'font-family': "'Orbitron', sans-serif",
        'letter-spacing': '0.25rem',
        display: 'inline-block',
        'transform-origin': 'center',
        'will-change': 'transform, text-shadow',
        'text-align': 'center',
        'max-width': '100%',
        'line-height': '1.2',
        'font-size': '1.25rem',
        'font-weight': '700',
      };
    }

    const color = (titleOpt && titleOpt.color) ? titleOpt.color : '#000000';
    const textShadow = (shadowOpt && shadowOpt.isActive)
      ? ((shadowOpt.blur ? '0 0 10px ' : '-1px 1px 10px ') + (shadowOpt.color || 'rgba(0,0,0,0.75)'))
      : 'none';

    return {
      color: color,
      'text-shadow': textShadow,
      animation: 'none',
      'font-size': '1.3rem',
      'font-weight': 'bold',
      display: '-webkit-box',
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'word-break': 'break-word',
      'white-space': 'normal',
      'line-height': '1.2',
      'max-height': '2.4em',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '2',
      'max-width': '100%',
      'font-family': "'AvenirNextCondensedBold', sans-serif",
    };
  }

  getCategoryStyle(game: any): { [key: string]: string } {
    if (!this.isCategoryActive(game)) {
      return { display: 'none' };
    }

    const categoryOpt = game && (game.categoryOpt || (game.gameMenuArt && game.gameMenuArt.categoryOpt));
    const shadowOpt = game && (game.shadowOpt || (game.gameMenuArt && game.gameMenuArt.shadowOpt));
    const isEfx = categoryOpt && categoryOpt.efx;
    const categoryColor =
      categoryOpt && categoryOpt.color && categoryOpt.color !== '#c4bfbf'
        ? categoryOpt.color
        : '#ff33cc';

    if (isEfx) {
      return {
        color: categoryColor,
        '--cat-glow-core': '#ffffff',
        '--cat-glow-color': categoryColor,
        '--cat-glow-pulse': '#ff66ee',
        '--cat-glow-deep': '#cc00ff',
        '--cat-glow-deep-pulse': '#dd33ff',
        'text-shadow': `0 0 12px #fff, 0 0 25px ${categoryColor}, 0 0 40px ${categoryColor}, 0 0 60px #cc00ff`,
        animation: 'fadeInQuickCategory 1.5s ease forwards, pulseFocusCategory 3s ease-in-out infinite 1.5s',
        'font-family': "'Orbitron', sans-serif",
        'letter-spacing': '0.22rem',
        display: 'inline-block',
        'transform-origin': 'center',
        'will-change': 'opacity, transform, text-shadow',
        'text-align': 'center',
        'font-size': '1rem',
        'font-weight': '600',
      };
    }

    const color = (categoryOpt && categoryOpt.color) ? categoryOpt.color : '#000000';
    const textShadow = (shadowOpt && shadowOpt.isActive)
      ? ((shadowOpt.blur ? '0 0 10px ' : '-1px 1px 10px ') + (shadowOpt.color || 'rgba(0,0,0,0.75)'))
      : 'none';

    return {
      color: color,
      'text-shadow': textShadow,
      animation: 'none',
      'letter-spacing': 'normal',
      'font-size': '14px',
      'font-weight': '500',
    };
  }

  getShadowStyle(game: any): string {
    const shadowOpt = game && (game.shadowOpt || (game.gameMenuArt && game.gameMenuArt.shadowOpt));
    if (!shadowOpt || !shadowOpt.isActive) {
      return 'none';
    }
    const color = shadowOpt.color || 'rgba(0, 0, 0, 0.5)';
    const blur = shadowOpt.blur ? '0 0 10px ' : '2px 2px 4px ';
    return blur + color;
  }

  getTitleTextStyle(game: any) {
    return getTitleTextStyle(game);
  }

  isSameEdge(game: any): boolean {
    return isSameEdge(game);
  }

  getSameEdgeContainerStyle(game: any, position: 'top' | 'bottom'): { [key: string]: string } {
    return getSameEdgeContainerStyle(game, position);
  }

  getTitlePositionStyle(game: any) {
    return getTitlePositionStyle(game);
  }

  getCategoryTextStyle(game: any) {
    return getCategoryTextStyle(game);
  }

  getCategoryPositionStyle(game: any) {
    return getCategoryPositionStyle(game);
  }

  getDrawDateColor(game: any): string {
    const titleOpt = getEffectiveTitleOpt(game);
    return titleOpt.color;
  }

  /**
   * Returns the version of `game` that should be displayed/acted on.
   * Backend (gamesWithDuplicateData) attaches `latestVersion` — a full
   * game record for the most recent restart, INCLUDING one still running —
   * when one exists. Falls back to the original game if latestVersion is
   * absent (never restarted, or backend hasn't populated it for this game
   * yet).
   */
  getDisplayGame(game: any): any {
    if (!game) return game;
    return game.latestVersion || game;
  }

  /**
   * Versions to LIST in the Restart dropdown. `allRelatedGames` is the
   * unfiltered family walk (running versions carry isLive: true);
   * `relatedGames` is the date-filtered fallback for responses from an
   * older backend. Delete paths must keep using `relatedGames` — the
   * date filter is what guarantees they can never reach a live game.
   */
  getDisplayVersions(game: any): any[] {
    if (!game) return [];
    return game.allRelatedGames || game.relatedGames || [];
  }

  getPrizeDisplayImage(prize: any): string {
    if (!prize) return '/assets/images/image-not-found-150x150.png';

    // Check Embed
    if (prize.image1 && prize.image1.type === 'embed') {
      const embedCode = prize.image1.image || "";
      const match = embedCode.match(/embed\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/0.jpg`;
      }
      return '/assets/images/image-not-found-150x150.png';
    }

    if (prize.type !== 'exact match') {
      if (prize.image1 && prize.image1.image) {
        const img = prize.image1.image;
        if (img.startsWith('http') || img.startsWith('data:')) return img;
        return this.s3BucketUrl + img;
      }
      return '/assets/images/image-not-found-150x150.png';
    } else {
      if (prize.image1 && prize.image1.image) {
        const img = prize.image1.image;
        if (img.startsWith('http') || img.startsWith('data:')) return img;
        return this.s3BucketUrl + img;
      }
      return '/assets/images/cash prize.png';
    }
  }
}
